import React, { useState } from 'react';
import { Plus, Trash2, Edit2, Check, X, Lock } from 'lucide-react';

const emptyForm = {
  nombre: '',
  descripcion: '',
  activo: true
};

export default function TiposDeduccionesTab({ deductionTypes, createDeductionType, updateDeductionType, deleteDeductionType }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleOpenCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setError('');
    setShowForm(true);
  };

  const handleOpenEdit = (t) => {
    setForm({ nombre: t.nombre, descripcion: t.descripcion || '', activo: t.activo });
    setEditingId(t.id);
    setError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) {
      setError('El nombre es obligatorio');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await updateDeductionType(editingId, form);
      } else {
        await createDeductionType(form);
      }
      setShowForm(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este tipo de deducción?')) return;
    try {
      await deleteDeductionType(id);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={handleOpenCreate}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold text-sm"
        >
          <Plus className="w-4 h-4" />
          Nuevo Tipo de Deducción
        </button>
      </div>

      {showForm && (
        <div className="border border-red-200 bg-red-50 rounded-xl p-5 space-y-3">
          <h4 className="font-semibold text-red-800">{editingId ? 'Editar Tipo de Deducción' : 'Nuevo Tipo de Deducción'}</h4>
          {error && <p className="text-sm text-red-600 bg-white px-3 py-2 rounded border border-red-200">{error}</p>}
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Nombre *</label>
              <input
                value={form.nombre}
                onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 outline-none"
                placeholder="Ej: Cooperativa"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Descripción</label>
              <input
                value={form.descripcion}
                onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 outline-none"
                placeholder="Descripción opcional"
              />
            </div>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.activo} onChange={e => setForm(f => ({ ...f, activo: e.target.checked }))} className="w-4 h-4 accent-red-600" />
              Activo
            </label>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-100">
              <X className="w-4 h-4 inline mr-1" />Cancelar
            </button>
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50">
              <Check className="w-4 h-4 inline mr-1" />{saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      )}

      <div className="overflow-hidden border border-gray-200 rounded-xl">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase text-gray-600">Nombre</th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase text-gray-600">Descripción</th>
              <th className="px-4 py-3 text-center text-xs font-bold uppercase text-gray-600">Estado</th>
              <th className="px-4 py-3 text-center text-xs font-bold uppercase text-gray-600">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {deductionTypes.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400 text-sm">No hay tipos de deducciones configurados</td></tr>
            ) : (
              deductionTypes.map(t => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-800">
                    {t.nombre}
                    {t.es_sistema && <Lock className="w-3 h-3 inline ml-1.5 text-gray-400" title="Sistema" />}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{t.descripcion || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${t.activo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {t.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => handleOpenEdit(t)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Editar">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      {!t.es_sistema && (
                        <button onClick={() => handleDelete(t.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Eliminar">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
